import {
  useRef, useEffect, useCallback,
} from 'react';
import {
  PathBrush, RectBrush, IBaseBrush,
  TEvent, TPointerEvent,
} from '@meitu/whee-infinite-canvas';
import useAIToolsCanvasStore, { setHasMask } from '@/store/useAIToolsCanvasStore';
import useAIWatermarkStore, {
  setSingleWatermarkMode,
  type ManualEraserType,
} from '@/store/useAIWatermarkStore';
import { useShallow } from 'zustand/react/shallow';

/** 手动消除模式：涂抹 | 框选 */
export type BrushMode = ManualEraserType;

/**
 * 笔刷管理 Hook（去水印版）
 * 支持涂抹（PathBrush）和框选（RectBrush）两种模式。
 */
export default function useBrushManager() {
  const brushRef = useRef<PathBrush | null>(null);
  const rectBrushRef = useRef<RectBrush | null>(null);

  const {
    brushSize,
    singleEraserLoading,
    manualEnableEraser,
    singleMediaType,
    singleWatermarkMode,
  } = useAIWatermarkStore(useShallow((state) => ({
    brushSize: state.brushSize,
    singleEraserLoading: state.singleEraserLoading,
    manualEnableEraser: state.manualEnableEraser,
    singleMediaType: state.singleMediaType,
    singleWatermarkMode: state.singleWatermarkMode,
  })));
  const singleManualWatermarkActive = singleWatermarkMode !== 'auto';
  // 自动模式下保留默认笔刷实例，但关闭真实绘制，切回手动后可立即恢复。
  const currentMode: BrushMode = singleManualWatermarkActive ? singleWatermarkMode : 'smear';

  const {
    render,
    imageTarget,
  } = useAIToolsCanvasStore(
    useShallow((state) => ({
      render: state.render,
      imageTarget: state.imageTarget,
    })),
  );

  useEffect(() => {
    brushRef.current?.setWidth(brushSize);
  }, [brushSize]);

  // 消除请求中或自动去水印模式下关闭涂抹；视频模式不支持画笔。
  useEffect(() => {
    if (!render?._FC) return;
    if (singleEraserLoading || singleMediaType === 'video' || !singleManualWatermarkActive) {
      render._FC.isDrawingMode = false;
    } else {
      render._FC.isDrawingMode = true;
    }
  }, [render, singleEraserLoading, singleManualWatermarkActive, singleMediaType]);

  // ---- 获取当前激活的笔刷 ----
  const getActiveBrush = (): IBaseBrush | null => {
    if (currentMode === 'rect') return rectBrushRef.current;
    return brushRef.current;
  };

  // ---- 初始化 PathBrush（涂抹）----
  const initPathBrush = () => {
    if (!render || !imageTarget) return;

    const bs = new PathBrush(render._FC, {
      shapeColor: '#F73E6C',
      shapeContainerName: 'eraser_mask_container',
      shapeContainerOpacity: 0.5,
      showShapeTitle: false,
      showShapeCloseBtn: false,
      targetElement: imageTarget,
      erase: false,
      width: brushSize,
      eraseShapeColor: '#ffffff',
      eraseShapeOpacity: 0.2,
      isMask: false,
      mergeOverlappingShapes: true,
      preserveExistingMasksOnSwitch: true,
    });
    brushRef.current = bs;
  };

  // ---- 初始化 RectBrush（框选）----
  const initRectBrush = () => {
    if (!render || !imageTarget) return;

    let tempRectCtx: CanvasRenderingContext2D | null = null;
    const bs = new RectBrush(render._FC, {
      shapeColor: '#F73E6C',
      shapeContainerName: 'eraser_mask_container',
      shapeContainerOpacity: 0.5,
      showShapeTitle: false,
      showShapeCloseBtn: false,
      targetElement: imageTarget,
      erase: false,
      isMask: false,
      mergeOverlappingShapes: true,
      preserveExistingMasksOnSwitch: true,
      render: (ctx: CanvasRenderingContext2D) => {
        const { viewportTransform } = bs.canvas;
        if (!bs.startPointer || !bs.endPointer) return;
        tempRectCtx = ctx;
        const minEdge = 10;
        const width = bs.endPointer.x - bs.startPointer.x;
        const height = bs.endPointer.y - bs.startPointer.y;
        const fakeStartPointer = bs.startPointer.transform(viewportTransform);
        const fakeEndPointer = bs.endPointer.transform(viewportTransform);
        const realWidth = fakeEndPointer.x - fakeStartPointer.x;
        const realHeight = fakeEndPointer.y - fakeStartPointer.y;
        const left = realWidth < 0 ? fakeStartPointer.x + realWidth : fakeStartPointer.x;
        const top = realHeight < 0 ? fakeStartPointer.y + realHeight : fakeStartPointer.y;

        bs.canvas.clearContext(ctx);
        ctx.save();
        ctx.beginPath();
        ctx.fillStyle = '#F73E6C';
        ctx.globalAlpha = 0.5;
        ctx.fillRect(left, top, Math.abs(realWidth), Math.abs(realHeight));
        ctx.stroke();
        ctx.restore();

        if (Math.abs(width) < minEdge || Math.abs(height) < minEdge) {
          bs.endPointer = null;
        }
      },
    });

    const _onMouseUp = bs.onMouseUp;
    bs.onMouseUp = ({ e }: TEvent<TPointerEvent>) => {
      try {
        return _onMouseUp.call(bs, { e });
      } finally {
        if (tempRectCtx) {
          bs.canvas.clearContext(tempRectCtx);
          tempRectCtx = null;
        }
      }
    };
    rectBrushRef.current = bs;
  };

  // ---- 销毁笔刷 ----
  const destroyBrush = useCallback(() => {
    if (!render) return;

    render._FC.isDrawingMode = false;
    brushRef.current?.destroy();
    rectBrushRef.current?.destroy();
    render._FC.freeDrawingBrush = undefined;
    brushRef.current = null;
    rectBrushRef.current = null;
    setHasMask(false);
  }, [render]);

  // 笔刷初始化
  useEffect(() => {
    if (!render || !imageTarget) return undefined;
    initRectBrush();
    initPathBrush();
    return () => {
      destroyBrush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [render, imageTarget]);

  // 模式切换
  useEffect(() => {
    if (!render || !imageTarget) return;
    if (singleMediaType === 'video') {
      render._FC.isDrawingMode = false;
      render._FC.freeDrawingCursor = 'default';
      return;
    }

    const canDraw = !singleEraserLoading && singleManualWatermarkActive;
    if (currentMode === 'rect' && rectBrushRef.current) {
      render._FC.freeDrawingBrush = rectBrushRef.current;
      render._FC.isDrawingMode = canDraw;
      render._FC.freeDrawingCursor = 'crosshair';
    }
    if (currentMode === 'smear' && brushRef.current) {
      render._FC.freeDrawingBrush = brushRef.current;
      render._FC.isDrawingMode = canDraw;
      render._FC.freeDrawingCursor = 'none';
    }
    getActiveBrush()?.setErase(manualEnableEraser);
  }, [currentMode, render, imageTarget, manualEnableEraser, singleEraserLoading, singleManualWatermarkActive, singleMediaType]);

  /** 切换手动工具时退出自动去水印模式。 */
  const setBrushMode = (nextMode: BrushMode) => {
    setSingleWatermarkMode(nextMode);
  };

  /**
   * 清除所有 mask（PathBrush + RectBrush）
   */
  const clearMask = () => {
    if (!render) return;

    brushRef.current?.clearTargetElement();
    rectBrushRef.current?.clearTargetElement();
    setHasMask(false);
    render._FC.fire('mask:history:changed');
  };

  return {
    setBrushMode,
    clearMask,
    destroyBrush,
    currentMode,
  };
}
